import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Hero from './Hero';
import Demo from './Demo';
import Features from './Features';
import LanguagesSection from './LanguagesSection';
import CTA from './CTA';
import NotFound from './NotFound';
import PageMeta from './seo/PageMeta';
import JsonLd from './seo/JsonLd';
import {
  LanguageLandingProvider,
  useLanguageLanding,
} from '../context/LanguageLandingContext';
import {
  getLanguageBySlug,
  languageLandingPath,
} from '../data/supportedLanguages';
import { getSiteOrigin } from '../utils/siteOrigin';
import { languageLandingHreflangAlternates } from '../utils/hreflang';
import i18n from '../i18n';

const LanguageLandingPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const language = getLanguageBySlug(slug);

  useEffect(() => {
    if (!language) return;
    const prev = i18n.language;
    void i18n.changeLanguage(language.lang);
    return () => {
      void i18n.changeLanguage(prev || 'en');
    };
  }, [language]);

  if (!language) {
    return <NotFound />;
  }

  return (
    <LanguageLandingProvider language={language}>
      <LanguageLandingInner />
    </LanguageLandingProvider>
  );
};

function LanguageLandingInner() {
  const { language } = useLanguageLanding();
  const { t } = useTranslation('landing');
  const origin = getSiteOrigin().replace(/\/$/, '');
  const hreflangAlternates = useMemo(
    () => languageLandingHreflangAlternates(origin),
    [origin]
  );

  if (!language) {
    return <NotFound />;
  }

  const path = languageLandingPath(language);
  const title = t('metaTitle');
  const description = t('metaDescription');
  const keywords = t('keywords');
  const htmlLang = t('htmlLang');

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: `${origin}${path}`,
    inLanguage: htmlLang,
    about: {
      '@type': 'SoftwareApplication',
      name: 'Substyle',
      applicationCategory: 'MultimediaApplication',
      operatingSystem: 'Adobe Premiere Pro',
    },
  };

  return (
    <>
      <PageMeta
        title={title}
        description={description}
        canonicalPath={path}
        keywords={keywords}
        htmlLang={htmlLang}
        hreflangAlternates={hreflangAlternates}
      />
      <JsonLd id={`substyle-lang-${language.slug}-jsonld`} data={jsonLd} />
      <Hero />
      <Demo />
      <Features />
      <LanguagesSection />
      <CTA />
    </>
  );
}

export default LanguageLandingPage;
